import { api, ApiError } from '$lib/shared/api/client';
import { normalizeAbn } from '$features/onboarding/services/validation';
import { createMyProfile as createProfile, getMyProfile } from '$features/profile/api/client';
import type {
  AbnVerificationResponse,
  CreateOrganisationRequest,
  CreateStripeConnectAccountRequest,
  HealthCareProfessionOption,
  Organisation,
  OrganisationSearchResult,
  StripeConnectAccountResponse,
  UserProfile,
  UserRole,
} from '$shared/types/api';

export { createProfile as createMyProfile };

export async function getUserRoles(): Promise<UserRole[]> {
  return api.get<UserRole[]>('/api/user-roles/list', { skipAuth: true });
}

export async function fetchMyProfileOrNull(): Promise<UserProfile | null> {
  try {
    return await getMyProfile();
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) {
      return null;
    }
    throw error;
  }
}

export interface UpdateAhpraRecordRequest {
  registrationNumber: string;
  postCode: string;
  healthCareProfessionId: string;
}

export async function updateAhpraRecord(payload: UpdateAhpraRecordRequest): Promise<void> {
  await api.post('/api/practitioner/update-ahpra-record', payload);
}

export async function getPrimaryProfessions(): Promise<HealthCareProfessionOption[]> {
  return api.get<HealthCareProfessionOption[]>('/api/app-data/list-primary-professions', {
    skipAuth: true,
  });
}

export async function searchOrganisations(query: string): Promise<OrganisationSearchResult[]> {
  const encoded = encodeURIComponent(query.trim());
  return api.get<OrganisationSearchResult[]>(`/api/organisation/search/${encoded}`);
}

export async function requestOrganisationJoin(organisationId: string): Promise<void> {
  await api.post(`/api/organisation/join-request/${organisationId}`, {});
}

export async function createOrganisation(payload: CreateOrganisationRequest): Promise<Organisation> {
  return api.post<Organisation>('/api/organisation', payload);
}

export async function verifyAbn(abnNumber: string): Promise<AbnVerificationResponse> {
  return api.get<AbnVerificationResponse>(`/api/stripe/abn-registration/${normalizeAbn(abnNumber)}`);
}

export async function loadStripeConnectAccount(): Promise<StripeConnectAccountResponse | null> {
  try {
    return await api.get<StripeConnectAccountResponse>('/api/stripe/load-stripe-connect-account');
  } catch (error) {
    if (error instanceof ApiError && (error.status === 404 || error.status === 400)) {
      return null;
    }
    throw error;
  }
}

export async function createStripeConnectAccount(payload: CreateStripeConnectAccountRequest): Promise<void> {
  await api.post('/api/stripe/create-connect-account', {
    BSB: payload.bsb,
    AccountNumber: payload.accountNumber,
    ABN: payload.abn,
    PlaceId: payload.placeId,
  });
}
